/**
 * The connection to the phone, kept where a window cannot take it with it.
 *
 * It used to live inside the Phones panel, which lives inside the Settings
 * dialog. Closing that dialog unmounted the panel, the panel's cleanup closed
 * the peer connection, and the phone went dead: the owner could not look at
 * their own settings without hanging up on themselves.
 *
 * So the connection lives here, at module level, for as long as the app is
 * open. The panel becomes a view of it: it subscribes when it appears and
 * unsubscribes when it goes, and neither of those touches the connection.
 * The only things that end it are the owner pressing disconnect, the phone
 * being revoked, and the app itself closing.
 *
 * A phone going quiet is no longer one of them. It used to be: the channel
 * closed, this called `stopHosting`, and the desktop forgot everything while
 * the owner was still holding the phone that had merely locked its screen.
 * Now the desktop goes and stands in the room the two agreed on and waits
 * there, re-opening it before it can expire, for as long as the app is open.
 *
 * Same shape as tunnel-link.js on the phone's side, for the same reason:
 * what the network is doing is not a fact about a component tree.
 */

import { noteConnection } from "./connection-log";
import { serveTunnel } from "./tunnel-host";
import { offerPairing } from "./tunnel-signin";
import { encodeFrame } from "./tunnel-protocol";
import { connect as connectDirect, openRoom } from "./direct-link";
import { HOST, forgetAppointment, keepAppointment, makeAppointment, readAppointmentFor } from "./rendezvous";

export const IDLE = "idle";
export const WAITING = "waiting";
export const SERVING = "serving";
export const FAILED = "failed";
/** A phone that was here and will be again: the appointment is being kept. */
export const EXPECTING = "expecting";

/** A phone being carried to another room and unlocked takes a while. */
export const WAIT_FOR_PHONE_MS = 120000;

/**
 * How long to stand in the appointment room before opening it again.
 *
 * Under the five-minute room expiry in `connect/rooms.py`, and under Cloud
 * Run's own limit on how long one request may last, because a signalling
 * socket is a request. Re-opening costs one socket and keeps the appointment
 * alive indefinitely without the service needing to know anything about it.
 */
export const KEEP_ROOM_MS = 110000;

/**
 * Silence on the channel before the phone counts as gone.
 *
 * The phone pings every twenty-five seconds while it is awake, so a minute of
 * nothing means it is not awake. Deliberately not waiting for the data channel
 * to fire `onclose`: that depends on WebRTC noticing its own liveness checks
 * have stopped, which takes its own time and, on a channel whose transport
 * died quietly, may not happen at all. Waiting at the appointment early costs
 * one socket. Waiting too late costs the phone a slow return.
 */
export const SILENCE_MS = 60000;

/**
 * How often to check that we are where we said we would be.
 *
 * The reconciliation tick. Everything else in this file reacts to something:
 * a channel closing, a watchdog counting silence. On the night of 28 July both
 * of those missed, proved from the connect service's own logs: after the
 * pairing at 23:23:51 the Mac fetched `/ice` exactly zero times before the next
 * code was shown, so `expectAgain` never ran once, and the phone knocked seven
 * times on a room nobody had opened.
 *
 * A reaction that can be missed is a promise that can be broken. So this does
 * not react. It states a fact and keeps making it true: if there is an
 * appointment and no phone is being served, the desktop is standing in that
 * room. Sleep, a dead channel nobody noticed, a restart: all the same case.
 */
export const RECONCILE_MS = 15000;

const watchers = new Set();
const blank = {
  where: IDLE,
  expecting: false,
  room: null,
  route: null,
  busy: 0,
  log: [],
  paired: null,
  error: null,
  detail: null,
};

let state = blank;
let link = null;
let serving = null;
let counting = null;
let settle = null;
let appointment = null;
let expecting = false;
/** The QR room being shown, if any. Separate from the appointment on purpose. */
let codeRoom = null;
/** Which code attempt is current, so a stale one cannot report over a new one. */
let codeTurn = 0;
let failure = null;
let supervisor = null;

/**
 * The working parts, with real ones from the start.
 *
 * These used to be filled in only by `startHosting`, which was fine while the
 * only way to end up standing at an appointment was to have shown a code first.
 * It is not fine now: after a restart the desktop has an appointment on disk
 * and has shown no code, so reconciliation runs with nothing wired and the
 * restart case, which is one of the three this exists for, would throw instead
 * of connecting.
 */
function defaultTools() {
  return {
    connectImpl: connectDirect,
    serveImpl: serveTunnel,
    pairImpl: offerPairing,
    call: null,
    setIntervalImpl: (fn, ms) => setInterval(fn, ms),
    setTimeoutImpl: (fn, ms) => setTimeout(fn, ms),
    makeImpl: makeAppointment,
    keepImpl: keepAppointment,
    note: noteConnection,
    keepRoomMs: KEEP_ROOM_MS,
    timeoutMs: WAIT_FOR_PHONE_MS,
    silenceMs: SILENCE_MS,
    now: () => Date.now(),
  };
}

let tools = defaultTools();

/**
 * What is true right now, derived rather than declared.
 *
 * It used to be assigned at each step, which is why showing a code could
 * silently mean "and therefore not waiting at the appointment". The two are
 * different facts about different rooms and both can hold at once; deriving
 * the label from the facts makes it impossible for one to overwrite the other.
 */
function whereNow() {
  if (link && serving) return SERVING;
  if (codeRoom) return WAITING;
  if (expecting) return EXPECTING;
  if (failure) return FAILED;
  return IDLE;
}

function announce(next = {}) {
  // `expecting` is reported next to `where` rather than folded into it,
  // because while a code is on screen both are true and the screen has to be
  // able to say so.
  state = { ...state, ...next, where: whereNow(), room: codeRoom, expecting };
  watchers.forEach((watcher) => watcher(state));
}

export function hostState() {
  return state;
}

/** Told whenever that changes. Returns the way to stop being told. */
export function watchHost(watcher) {
  watchers.add(watcher);
  watcher(state);
  // Unsubscribing deliberately does nothing else. A screen going away is not
  // a reason to hang up on somebody's phone.
  return () => watchers.delete(watcher);
}

/**
 * Show a code, wait for the phone, then be the machine it uses.
 *
 * Everything that touches the outside is injectable so the whole thing can
 * be driven in a test without a network, a browser or a phone.
 */
export async function startHosting({
  connectImpl = connectDirect,
  openRoomImpl = openRoom,
  serveImpl = serveTunnel,
  pairImpl = offerPairing,
  call,
  timeoutMs = WAIT_FOR_PHONE_MS,
  setIntervalImpl = setInterval,
  setTimeoutImpl = setTimeout,
  makeImpl = makeAppointment,
  keepImpl = keepAppointment,
  note = noteConnection,
  keepRoomMs = KEEP_ROOM_MS,
  silenceMs = SILENCE_MS,
  now = () => Date.now(),
} = {}) {
  // Deliberately NOT stopHosting(). That is the whole of this change.
  //
  // Showing a code used to begin by tearing everything down, including the
  // appointment the desktop was standing at. On 28 July the connect logs show a
  // code room opening within five to fifty seconds of nearly every appointment
  // room, which means the owner, looking at a phone that would not connect,
  // pressed the one button on screen and thereby removed the only thing that
  // could have let the phone back in. The cure was the disease.
  //
  // A code and an appointment are two rooms. There is no reason a machine
  // cannot stand in both, and every reason it should: the code is for a new
  // phone, the appointment is for the one already paired.
  hangUpServed();
  cancelCode();
  failure = null;
  tools = {
    connectImpl, serveImpl, pairImpl, call,
    // Wrapped, not stored bare, and this line is the whole of a bug that cost
    // Frank an evening. `setInterval` is a method of `window` and refuses to
    // run with any other receiver. Kept as `tools.setIntervalImpl` and called
    // as `tools.setIntervalImpl(...)`, its receiver is this object, and the
    // browser answers "TypeError: Illegal invocation". That threw inside
    // beServer before the pairing offer was ever made, so the phone waited for
    // an offer that was never sent, fell through to the sign-in screen, and
    // the Mac sat on "Signing your phone in..." forever.
    //
    // An arrow calls the original with no receiver at all, which is how it was
    // called before these moved onto an object.
    setIntervalImpl: (fn, ms) => setIntervalImpl(fn, ms),
    setTimeoutImpl: (fn, ms) => setTimeoutImpl(fn, ms),
    makeImpl, keepImpl, note, keepRoomMs, timeoutMs, silenceMs, now,
  };
  const opened = openRoomImpl();
  const turn = ++codeTurn;
  codeRoom = opened;
  announce({ route: null, busy: 0, log: [], paired: null, error: null, detail: null });

  let made = null;
  try {
    made = await connectImpl({
      role: "host",
      room: opened.room,
      secret: opened.secret,
      timeoutMs,
    });
  } catch (e) {
    // A code nobody scanned is not a reason to stop waiting at the appointment,
    // so this reports the code and touches nothing else.
    if (turn !== codeTurn) return null;
    codeRoom = null;
    failure = e;
    announce({ error: e.message || "No connection was made.", detail: e.attempt || null });
    return null;
  }

  if (turn !== codeTurn) {
    // Another code, or a disconnect, happened while this one was in the air.
    try { made.close(); } catch { /* already gone */ }
    return null;
  }

  codeRoom = null;
  link = made;
  await beServer(link, { pair: true });
  return link;
}

/** Forget any code attempt in flight, without touching the appointment. */
function cancelCode() {
  codeTurn += 1;
  codeRoom = null;
}

/** Hang up a phone that is being served, without touching the appointment. */
function hangUpServed() {
  clearInterval(counting);
  clearTimeout(settle);
  counting = null;
  settle = null;
  serving = null;
  try {
    link?.close();
  } catch {
    // already gone
  }
  link = null;
}

/**
 * Serve this channel, and agree where to meet if it dies.
 *
 * The appointment is made and sent first, before anything else can go wrong,
 * because it is worth nothing unless it was sent while the channel still
 * worked. That is the whole trick: the way back is arranged in advance, by
 * the two devices, over the connection they already have.
 */
async function beServer(current, { pair }) {
  link = current;
  // A phone is here, so neither of the two waiting rooms is wanted any more.
  // The appointment loop notices on its next turn and closes what it is
  // holding; the fresh appointment made below replaces the old one regardless.
  expecting = false;
  cancelCode();
  failure = null;
  // Served before announced: with the label derived from the facts, a phone
  // that is here but not yet being served reads as nobody at all, and the
  // screen would blink through idle on its way to serving.
  // Fix afscheid: een telefoon die doei zegt, laat de Mac meteen naar de
  // afgesproken kamer gaan in plaats van op de stilte te wachten.
  serving = tools.serveImpl({ channel: current.channel, onBye: () => expectAgain() });
  announce({ error: null, detail: null });

  try {
    const next = tools.makeImpl();
    current.channel.send(encodeFrame({ kind: "rendezvous", id: next.room, secret: next.secret }));
    tools.keepImpl({ ...next, side: HOST });
    appointment = { ...next, side: HOST };
    tools.note("appointment-kept", "");
  } catch {
    // Making it needs real randomness, sending it needs a live channel, and
    // keeping it needs somewhere to write. Any of the three can be missing on
    // some browser somewhere, and none of them is a reason to fail a
    // connection that is working: without an appointment this behaves exactly
    // as it did before there was one, and the owner scans the code again.
    appointment = null;
  }

  current.channel.onClosed?.(() => expectAgain());

  // Everything from here to the pairing is housekeeping: a counter, a route
  // label, a watchdog. None of it is why the phone is here.
  //
  // It sits in a try because on 29 July it did not, and one throw inside it
  // meant the pairing offer below was never made at all. The phone waited for
  // an offer that was never coming, gave up after twelve seconds, and showed a
  // sign-in screen; the Mac sat on "Signing your phone in..." for as long as
  // anyone cared to watch. Signing the phone in is the point of this function
  // and nothing optional above it may be able to prevent it.
  try {
    counting = tools.setIntervalImpl(() => {
      if (!serving) return;
      announce({ busy: serving.busy, log: serving.log });
      // Only a phone that pings can be judged by its silence. Without this an
      // older phone, which never pings, would be hung up on after a minute of
      // the owner simply not tapping anything.
      if (!serving.heardPing) return;
      const quiet = tools.now() - (serving.lastHeard ?? tools.now());
      if (quiet > (tools.silenceMs ?? SILENCE_MS)) {
        tools.note("ping-late", `${Math.round(quiet / 1000)} s stil`);
        expectAgain();
      }
    }, 500);

    // Asked twice: a connection that opens through the relay sometimes finds a
    // direct path a few seconds later.
    current.path?.().then((route) => announce({ route }));
    settle = tools.setTimeoutImpl(() => link?.path?.().then((route) => announce({ route })), 5000);
  } catch (e) {
    announce({ detail: `Some of the connection display did not start: ${e.message}` });
  }

  if (!pair) return;
  tools
    .pairImpl({
      send: (frame) =>
        current.channel.send(encodeFrame({ kind: "pairing", id: frame.offer, secret: frame.secret })),
      call: tools.call,
    })
    .then((result) => announce({ paired: result.paired ? result.device : false }))
    .catch(() => announce({ paired: false }));
}

/**
 * The phone went quiet. Go and stand where it will look.
 *
 * Not an error and not the end of anything. A locked phone is the ordinary
 * case, and this is the desktop waiting rather than hanging up. It re-opens
 * the room before it can expire and keeps waiting until the phone arrives or
 * the owner stops it.
 */
async function expectAgain() {
  if (expecting) return;
  hangUpServed();

  const meeting = appointment || readAppointmentFor(HOST);
  if (!meeting) {
    tools.note?.("channel-closed", "no appointment");
    announce({ route: null, busy: 0, log: [], paired: null, error: null, detail: null });
    return;
  }

  expecting = true;
  tools.note?.("channel-closed", "waiting at the appointment");
  announce({ busy: 0, error: null, detail: null });

  while (expecting) {
    let opened = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      opened = await tools.connectImpl({
        role: "host",
        room: meeting.room,
        secret: meeting.secret,
        timeoutMs: tools.keepRoomMs ?? KEEP_ROOM_MS,
      });
    } catch {
      // Nobody came within the window. The room is about to expire, so open
      // it again rather than leave the appointment quietly dead.
      continue;
    }
    if (!expecting) {
      try { opened.close(); } catch { /* stopped mid-handshake */ }
      return;
    }
    expecting = false;
    tools.note?.("channel-open", "phone came back");
    // No pairing this time. This phone was paired when it first arrived and
    // still holds its token; offering again would add a second entry to the
    // owner's device list every time their screen locked.
    // eslint-disable-next-line no-await-in-loop
    await beServer(opened, { pair: false });
    return;
  }
}

/**
 * Make the promise true, whatever happened to get here.
 *
 * The one rule: if this machine agreed an appointment and is not serving a
 * phone, it is standing in that room. No event is consulted, because the
 * events are what failed. Safe to call as often as anyone likes; it does
 * nothing at all in the ordinary case.
 *
 * The four cases it covers, which used to need three separate mechanisms and
 * a fourth that did not exist:
 *  - the channel died and nothing noticed (28 July, proved from the logs)
 *  - the Mac slept with its lid shut and woke to an appointment it had left
 *  - the app was restarted and its memory of the appointment went with it
 *  - a code was shown, which used to abandon the appointment outright
 */
export function ensureAtAppointment({ readImpl = readAppointmentFor, ...over } = {}) {
  if (Object.keys(over).length) tools = { ...tools, ...over };
  if (expecting) return false; // already standing there
  if (link && serving && link.channel?.ready !== false) return false; // a phone is really here

  // Believing we are serving a phone whose channel is shut is exactly the
  // state that lost the night of 28 July: the desktop sat on SERVING with a
  // dead channel while the phone knocked on an empty room.
  if (link && link.channel?.ready === false) {
    tools.note?.("channel-closed", "found shut on a check, not by an event");
  }

  const meeting = appointment || readImpl(HOST);
  if (!meeting) return false;
  appointment = meeting;
  expectAgain();
  return true;
}

/**
 * Keep checking. Started once, by the desktop half of the app.
 *
 * A plain repeating check rather than a listener for waking up: `visibilitychange`
 * does not fire for a window that was never hidden, and a Mac coming back from
 * a shut lid gives the page no event of its own worth trusting. A tick that
 * simply runs again is true in all of those cases and needs no special ones.
 */
export function startAppointmentSupervisor({ everyMs = RECONCILE_MS, setIntervalImpl = setInterval, ...over } = {}) {
  stopAppointmentSupervisor();
  ensureAtAppointment(over);
  supervisor = setIntervalImpl(() => ensureAtAppointment(over), everyMs);
  return supervisor;
}

export function stopAppointmentSupervisor({ clearIntervalImpl = clearInterval } = {}) {
  if (supervisor !== null) clearIntervalImpl(supervisor);
  supervisor = null;
}

/** The owner pressing disconnect, or the app closing. */
export function stopHosting({ forget = false } = {}) {
  expecting = false;
  cancelCode();
  failure = null;
  hangUpServed();
  if (forget) {
    appointment = null;
    forgetAppointment();
  }
  if (state.where !== IDLE) announce({ ...blank });
}

/** Whether a phone is on the other end right now. */
export function isHosting() {
  return state.where === SERVING;
}

/** Whether a phone is expected back. */
export function isExpecting() {
  return state.where === EXPECTING;
}
